/* import __COLOCATED_TEMPLATE__ from './related-content-view-panel.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import type { EntityType } from 'embercom/models/data/entity-types';
import type { RelatedContentEntity } from 'embercom/services/suggestion-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { Block } from 'embercom/models/common/blocks/block';
import { action } from '@ember/object';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type RouterService from '@ember/routing/router-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';

interface Args {
  showSidePanel: boolean;
  onClose: () => void;
  entity: RelatedContentEntity | null;
  entityTypeName: string;
}

export default class RelatedContentViewPanel extends Component<Args> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare router: RouterService;

  fetchContentTask = taskFor(this.loadEntityContent);
  entityContentData = trackedTask(this, this.fetchContentTask, () => [
    this.args.entity?.entityId,
    this.args.entity?.entityType,
  ]);

  @restartableTask
  *loadEntityContent(entityId?: number, entityType?: EntityType): TaskGenerator<Array<Block>> {
    if (!entityId || !entityType) {
      return [];
    }
    let response = yield this.knowledgeHubService.findContent({
      contentId: entityId,
      contentType: entityType,
      peekFirst: true,
    });

    if (!response) {
      return [];
    }

    let content = response.currentVersion?.localisedJsonBlocks ?? response.jsonBlocks;

    if (content && !Array.isArray(content)) {
      content = content.serialize().toArray();
    }

    return content ?? [];
  }

  get content(): Array<Block> {
    return this.entityContentData.value as Array<Block>;
  }

  @action
  openInKnowledge() {
    if (this.args.entity) {
      safeWindowOpen(
        this.router.urlFor(
          'apps.app.knowledge-hub.view',
          objectNames[this.args.entity?.entityType],
          this.args.entity?.entityId,
        ),
        '_blank',
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::RelatedContentViewPanel': typeof RelatedContentViewPanel;
    'reporting/automation/ai-insights/related-content-view-panel': typeof RelatedContentViewPanel;
  }
}
